import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type ThemeMode = 'light' | 'dark';

export interface ThemeColors {
  // Primary colors from product.pdf
  primary: string;
  primaryHover: string;
  primaryActive: string;

  // Background colors
  background: string;
  backgroundSecondary: string;
  backgroundTertiary: string;

  // Surface colors
  surface: string;
  surfaceHover: string;
  surfaceActive: string;

  // Text colors
  textPrimary: string;
  textSecondary: string;
  textTertiary: string;
  textInverse: string;

  // Border colors
  border: string;
  borderLight: string;
  borderStrong: string;

  // Status colors
  success: string;
  warning: string;
  error: string;
  info: string;

  // Shadow
  shadow: string;
  shadowLight: string;
  shadowStrong: string;
}

export const lightTheme: ThemeColors = {
  // Teal primary color - updated to #1A979B
  primary: '#1A979B',
  primaryHover: '#157a7d',
  primaryActive: '#126567',

  // Clean light backgrounds
  background: '#FFFFFF',
  backgroundSecondary: '#F8F9FA',
  backgroundTertiary: '#E9ECEF',

  // Surface colors
  surface: '#FFFFFF',
  surfaceHover: '#F8F9FA',
  surfaceActive: '#E9ECEF',

  // Text colors
  textPrimary: '#212529',
  textSecondary: '#6C757D',
  textTertiary: '#ADB5BD',
  textInverse: '#FFFFFF',

  // Borders
  border: '#DEE2E6',
  borderLight: '#F1F3F4',
  borderStrong: '#ADB5BD',

  // Status colors
  success: '#28A745',
  warning: '#FFC107',
  error: '#DC3545',
  info: '#17A2B8',

  // Shadows
  shadow: 'rgba(0, 0, 0, 0.1)',
  shadowLight: 'rgba(0, 0, 0, 0.05)',
  shadowStrong: 'rgba(0, 0, 0, 0.15)'
};

export const darkTheme: ThemeColors = {
  // Same teal primary for consistency - updated to #1A979B
  primary: '#1A979B',
  primaryHover: '#2AABAD',
  primaryActive: '#157a7d',

  // Dark backgrounds
  background: '#1A1D23',
  backgroundSecondary: '#2D3138',
  backgroundTertiary: '#343A46',

  // Dark surface colors
  surface: '#2D3138',
  surfaceHover: '#343A46',
  surfaceActive: '#3D4450',

  // Dark text colors
  textPrimary: '#F8F9FA',
  textSecondary: '#ADB5BD',
  textTertiary: '#6C757D',
  textInverse: '#212529',

  // Dark borders
  border: '#495057',
  borderLight: '#343A46',
  borderStrong: '#6C757D',

  // Status colors (slightly adjusted for dark theme)
  success: '#20C997',
  warning: '#FD7E14',
  error: '#E74C3C',
  info: '#17A2B8',

  // Dark shadows
  shadow: 'rgba(0, 0, 0, 0.25)',
  shadowLight: 'rgba(0, 0, 0, 0.15)',
  shadowStrong: 'rgba(0, 0, 0, 0.4)'
};

export interface ThemeContextType {
  mode: ThemeMode;
  colors: ThemeColors;
  toggleTheme: () => void;
  setTheme: (mode: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  // Initialize theme from localStorage or system preference
  const getInitialTheme = (): ThemeMode => {
    if (typeof window !== 'undefined') {
      const savedTheme = localStorage.getItem('mabat3d-theme') as ThemeMode;
      if (savedTheme && (savedTheme === 'light' || savedTheme === 'dark')) {
        return savedTheme;
      }

      // Check system preference
      if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
        return 'dark';
      }
    }
    return 'light';
  };

  const [mode, setMode] = useState<ThemeMode>(getInitialTheme);
  const colors = mode === 'light' ? lightTheme : darkTheme;

  const toggleTheme = () => {
    const newMode = mode === 'light' ? 'dark' : 'light';
    setTheme(newMode);
  };

  const setTheme = (newMode: ThemeMode) => {
    setMode(newMode);
    if (typeof window !== 'undefined') {
      localStorage.setItem('mabat3d-theme', newMode);
    }
  };

  // Apply CSS custom properties to root element
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const root = document.documentElement;

      // Set data-theme attribute for CSS selector [data-theme="dark"]
      root.setAttribute('data-theme', mode);

      // The CSS variables are already defined in theme.css
      // No need to set them dynamically since they're controlled by [data-theme] selector
    }
  }, [mode]);

  // Listen for system theme changes
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleSystemThemeChange = (e: MediaQueryListEvent) => {
        // Only auto-switch if no saved preference exists
        if (!localStorage.getItem('mabat3d-theme')) {
          setMode(e.matches ? 'dark' : 'light');
        }
      };

      mediaQuery.addEventListener('change', handleSystemThemeChange);
      return () => mediaQuery.removeEventListener('change', handleSystemThemeChange);
    }
  }, []);

  const value: ThemeContextType = {
    mode,
    colors,
    toggleTheme,
    setTheme
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};